/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useState } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';

import SettingsSlider from 'components/@common/Slider';
import SettingsCard from 'components/@common/Cards/SettingsCard';
import { WeekSettingData, Classroom } from 'types/schema-generated-types';
import GET_KEYBOARDING_CLASSROOM_SETTINGS from 'graph/settings/key/get-keyboarding-classroom-settings-query';
import UPDATE_KEY_CLASSROOM_STOP_AND_REVIEW_SETTING from 'graph/settings/key/update-key-classroom-stop-and-review-setting-mutation';
import { updateCache } from 'graph/update-cache';
import useStore from 'store';
import getSettingSuccessAlert from '../../../utils/settings-success-alert';

interface StopAndReviewProps {
  classroom: Classroom;
  expanded: string | null;
  setting: WeekSettingData | null;
  handleChangeExpanded: (panel: string | null) => () => void;
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      paddingTop: theme.spacing(2),
      width: '100%'
    },
    switch: { marginLeft: 0 },
    sliderLabelTitle: { fontWeight: theme.typography.fontWeightRegular },
    sliderLabelValue: { fontWeight: theme.typography.fontWeightBold }
  };
});

const getClassroomKeySettingsMutationCacheUpdate = (classroomId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_KEYBOARDING_CLASSROOM_SETTINGS,
      variables: {
        where: {
          classroomId
        }
      },
      reducer: (
        cachedData,
        { updateKeyClassroomSettingStopAndReview }
      ): { keyClassroomSettings: any } => {
        const [{ value: stopAndReview }] = updateKeyClassroomSettingStopAndReview;
        return {
          keyClassroomSettings: {
            ...cachedData?.keyClassroomSettings,
            stopAndReview
          }
        };
      }
    }
  ])
});

const StopAndReview: FC<StopAndReviewProps> = ({
  classroom,
  expanded,
  setting,
  handleChangeExpanded
}) => {
  const STOP_AND_REVIEW_MAX_WEEK = 36;
  const { t } = useTranslation('StopAndReviewSetting');
  const classes = useStyles();

  const { id: classroomId } = classroom;
  const [, { openAlert }] = useStore();
  const [updateKeyClassroomSettingStopAndReview] = useMutation(
    UPDATE_KEY_CLASSROOM_STOP_AND_REVIEW_SETTING,
    getClassroomKeySettingsMutationCacheUpdate(+classroomId)
  );

  const stopReviewValue: any = {
    enabled: false,
    currentWeek: STOP_AND_REVIEW_MAX_WEEK
  };
  const [enableStopAndReview, setEnableStopAndReview] = useState(
    setting?.enabled || stopReviewValue.enabled
  );
  const [value, setValue] = useState<number | number[]>(
    setting?.week || stopReviewValue.currentWeek
  );

  const handleChangeSlider = (event: any, newValue: number | number[]): void => {
    setValue(newValue);
  };

  const handleToggle = (): void => setEnableStopAndReview(!enableStopAndReview);

  const handleOnCancel = (): void => {
    setEnableStopAndReview(setting?.enabled || stopReviewValue.enabled);
    setValue(setting?.week || stopReviewValue.currentWeek);
  };

  const handleOnConfirm = async (): Promise<void> => {
    try {
      await updateKeyClassroomSettingStopAndReview({
        variables: {
          data: {
            classroomId,
            value: {
              enabled: enableStopAndReview,
              week: value
            }
          }
        }
      });
      openAlert(getSettingSuccessAlert('Stop & Review', t));
    } catch (error) {
      //TODO
      console.log('Error');
    }
  };

  return (
    <SettingsCard
      title={t('Stop & Review')}
      panel="stopAndReview"
      description={t(
        'Set a point in the curriculum for the whole class to stop and review previously completed activities.'
      )}
      expanded={expanded}
      handleChangeExpanded={handleChangeExpanded}
      onCancel={handleOnCancel}
      onSubmit={handleOnConfirm}
    >
      <FormControl component="fieldset" className={classes.container}>
        <FormControlLabel
          className={classes.switch}
          control={
            <Switch
              checked={enableStopAndReview}
              onChange={handleToggle}
              name="stopAndReview"
              color="primary"
            />
          }
          label={enableStopAndReview ? t('Enabled') : t('Disabled')}
        />
        {enableStopAndReview && (
          <div>
            <Typography id="stop-and-review-slider" gutterBottom variant="h6">
              <Typography variant="body1" component="p">
                {t(
                  'Move the slider to the week you want the students to stop and review previously completed activities.'
                )}
              </Typography>
              <Typography
                component="span"
                className={classes.sliderLabelTitle}
              >{`Current Week: `}</Typography>
              <Typography component="span" className={classes.sliderLabelValue}>
                {value}
              </Typography>
            </Typography>
            <SettingsSlider
              defaultValue={0}
              aria-labelledby="stop-and-review-slider"
              value={value}
              valueLabelDisplay="on"
              marks
              min={0}
              max={STOP_AND_REVIEW_MAX_WEEK}
              onChange={handleChangeSlider}
            />
          </div>
        )}
      </FormControl>
    </SettingsCard>
  );
};

export default StopAndReview;
